import React, { useCallback, useState, useRef, useEffect, Fragment, useMemo } from "react"
import { Box, Typography, Button } from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { ConfirmationStepProps } from "./types"
import {
  COPY,
  DAYS,
  AUTO_CLOSE_INTERVAL,
  SUPPORT_HOURS_START,
  SUPPORT_HOURS_END,
} from "./constants"
import { formatTimeRange } from "./utils"

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ handleFinish }) => {
  const [secondsRemaining, setSecondsRemaining] = useState(10)
  const autoCloseIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const formattedSupportHours = useMemo(
    () => formatTimeRange(SUPPORT_HOURS_START, SUPPORT_HOURS_END),
    []
  )

  const renderHours = useCallback(
    (day: keyof typeof COPY.CONFIRMATION.DAYS) => {
      const isWeekend = day === "SATURDAY" || day === "SUNDAY"
      return isWeekend ? COPY.CONFIRMATION.APPOINTMENT_ONLY : formattedSupportHours
    },
    [formattedSupportHours]
  )

  useEffect(() => {
    autoCloseIntervalRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          if (autoCloseIntervalRef.current) {
            clearInterval(autoCloseIntervalRef.current)
          }
          setTimeout(() => handleFinish(), 500)
          return 0
        }
        return prev - 1
      })
    }, AUTO_CLOSE_INTERVAL)

    return () => {
      if (autoCloseIntervalRef.current) {
        clearInterval(autoCloseIntervalRef.current)
      }
    }
  }, [handleFinish])

  const handleManualFinish = () => {
    if (autoCloseIntervalRef.current) {
      clearInterval(autoCloseIntervalRef.current)
    }
    handleFinish()
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", fontSize: "48px", gap: 1, mb: 1 }}>
        <CheckCircleIcon color="primary" fontSize="inherit" />
        <Typography variant="h4" gutterBottom>
          {COPY.CONFIRMATION.TITLE}
        </Typography>
      </Box>
      <Typography color="text.secondary" variant="body1" mb={2.5} paragraph>
        {COPY.CONFIRMATION.MESSAGE}
      </Typography>
      <Box sx={{ textAlign: "left", mb: 2.5, p: 2 }}>
        <Typography color="text.secondary" variant="h6">
          {COPY.CONFIRMATION.SUPPORT_TEAM}
        </Typography>
        <Typography color="text.secondary" variant="body2" paragraph>
          {COPY.CONFIRMATION.SUPPORT_INFO}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            mt: 2,
          }}
        >
          {DAYS.map((day) => (
            <Fragment key={day}>
              <Typography color="text.secondary" variant="body2">
                {COPY.CONFIRMATION.DAYS[day]}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {renderHours(day)}
              </Typography>
            </Fragment>
          ))}
        </Box>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
          {COPY.CONFIRMATION.VOICEMAIL_MESSAGE}
        </Typography>
      </Box>
      <Button
        sx={{ marginLeft: "auto" }}
        variant="contained"
        color="primary"
        onClick={handleManualFinish}
      >
        {COPY.CONFIRMATION.FINISH}
      </Button>
      <Box sx={{ mt: 3, width: "100%" }}>
        <Typography align="center" variant="body2" color="primary" sx={{ mb: 1 }}>
          {COPY.CONFIRMATION.AUTO_CLOSE.replace("{seconds}", secondsRemaining.toString())}
        </Typography>
        <Box
          sx={{
            width: "100%",
            bgcolor: "#e0e0e0",
            borderRadius: 1,
            height: 5,
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${(secondsRemaining / 10) * 100}%`,
              bgcolor: "primary.main",
              borderRadius: 1,
              transition: "width 1s linear",
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ConfirmationStep
